import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import {
    IRequest,
    IBrand,
    ICountry,
    IRequestStatus,
    IUser,
    Role,
    IRequestResponse,
    IRequestFile,
    RequestFileTypes,
    IRequestFilters,
    ITheme,
} from "@/models";
import { IStatusLookup, userRequestStatusLookup, adminRequestStatusLookup, vipRequestStatusLookup } from "./IStatusLookup";
import Paginate from "vuejs-paginate";
import Datepicker from "vuejs-datepicker";
import { cacheService } from "@/services/CacheService";
import * as icons from "@/components/icons";

@Component({
    name: "list-requests",
    components: {
        Paginate,
        Datepicker,
        "search-loop-icon": icons.SearchLoopIcon,
    },
})
export default class ListRequests extends Vue {
    @Prop(({ default: false }))
    public archives: boolean;

    public promises: Array<Promise<any>>;
    public pageId: number;
    public filters: IRequestFilters;
    public loadingRequests: boolean;
    private readonly pageSize: number = 10;
    private extensionRegexp = new RegExp("(?:\.([^.]+))?$");

    constructor() {
        super();
        this.promises = [];

        this.pageId = 0;
        this.loadingRequests = false;
        if (!this.$store.state.app.user) {
            this.promises.push(this.$store.dispatch("app/getUser"));
        }

        if (!this.$store.state.request.isReferenceDataLoaded) {
            this.promises.push(this.$store.dispatch("request/fetchReferenceData"));
        }
        this.retrieveFilters();
    }

    public get title(): string {
        // Enable Deleted request in list only for Admin
        const archivesTitle: string = this.user?.role === Role.Admin ? "Archives & Deleted" : "Archives";
        return this.archives ? archivesTitle : "All requests";
    }

    public get pageCount(): number {
        return Math.ceil(this.userRequests.totalVolume / this.pageSize);
    }

    public get brands(): IBrand[] {
        return this.$store.getters["request/getBrands"];
    }

    public get countries(): ICountry[] {
        return this.$store.getters["request/getCountries"];
    }

    public get themes(): ITheme[] {
        return this.$store.getters["request/getThemes"];
    }

    public get userRequests(): IRequestResponse {
        return this.$store.state.request.userRequests;
    }

    public get requestsList(): IRequest[] {
        // Enable Deleted request in list only for Admin
        if (this.user?.role === Role.Admin) {
            return this.userRequests?.requests.filter(r =>
                r.archive === this.archives || r.deleted === this.archives) ?? [];
        } else {
            return this.userRequests?.requests.filter(r => r.archive === this.archives) ?? [];
        }
    }

    public get requestStatus(): IRequestStatus[] {
        return this.$store.getters["request/getRequestStatus"].map((x: IRequestStatus) => ({
            ...x,
            value: this.$options.filters.displayedStatus(x, this.user?.role),
        })) ?? [];
    }

    public get requestStatusLookup(): IStatusLookup[] {
        if (this.user?.role === Role.User) {
            return userRequestStatusLookup;
        } else if (this.user?.role === Role.Vip) {
            return vipRequestStatusLookup;
        } else if (this.user?.role === Role.Admin) {
            return adminRequestStatusLookup;
        } else {
            return [];
        }
    }

    public get user(): IUser {
        return this.$store.state.app.user;
    }

    public get isVip(): boolean {
        return this.user?.role === Role.Vip;
    }

    @Watch("archives")
    public async onArchivesChanges(newVal: boolean[], oldVal: boolean[]): Promise<void> {
        this.retrieveFilters();
        this.navigateToPage(this.filters.currentPage);
    }

    public getRequestStatusClass(request: IRequest): string {
        return request?.deleted ? "deleted" :
            this.$options.filters
                .displayedStatus(request?.requestStatus, this.user?.role)
                .replace(" ", "-")
                .toLowerCase();
    }

    public displayedRequestStatus(request: IRequest): string {
        return request?.deleted ?
            "Deleted" :
            this.$options.filters.displayedStatus(request?.requestStatus, this.user?.role);
    }

    public getRequestAdvice(request: IRequest): string {
        return !!request?.advice ? "Advice" : "Request";
    }

    public filterRequests() {
        this.cacheFilters();
        this.navigateToPage(1);
    }

    public async navigateToPage(page: number): Promise<void> {
        this.pageId = page - 1;
        const query: Array<[string, string]> = this.getQueryParameters(this.pageSize);
        this.loadingRequests = true;
        await this.$store.dispatch("request/fetchUserRequests", query);
        this.filters.currentPage = page;
        this.cacheFilters();
        this.loadingRequests = false;
    }

    public getCoverFile(files: IRequestFile[]): IRequestFile[] {
        return files?.filter(f => f.fileType === RequestFileTypes.Cover);
    }

    public getImage(file: IRequestFile): string {
        const extension: string = this.extensionRegexp.exec(file?.fileName)[1];
        return this.isImage(extension) ? file.uri : "";
    }

    public getFileName(file: IRequestFile): string {
        return file?.fileName;
    }

    public redirectTo(requestId: number): void {
        this.$router.push({ path: `/requests/${requestId}` });
    }

    public getImageClass(file: IRequestFile): string {
        const extension: string = this.extensionRegexp.exec(file?.fileName)[1];
        if (this.isImage(extension)) {
            return "image";
        } else {
            return this.isExtensionType(extension) + " unknown-file-type";
        }
    }

    // Hook.
    public async created(): Promise<void> {
        this.loadingRequests = true;
        await Promise.all(this.promises);
        this.navigateToPage(this.filters.currentPage);
        this.loadingRequests = false;
    }

    private getQueryParameters(pageSize: number): Array<[string, string]> {
        const query: Array<[string, string]> = [];
        query.push(["pageSize", String(pageSize)]);

        // Check null and 0.
        if (!!this.pageId) {
            query.push(["pageId", String(this.pageId)]);
        }

        if (this.filters.requestText !== null && this.filters.requestText !== undefined && this.filters.requestText.length > 0) {
            query.push(["requestTextContains", String(this.filters.requestText)]);
        }

        if (this.filters.brandId !== null && this.filters.brandId !== undefined) {
            query.push(["brandId", String(this.filters.brandId)]);
        }

        if (this.filters.countryId !== null && this.filters.countryId !== undefined) {
            query.push(["countryId", String(this.filters.countryId)]);
        }

        if (this.filters.themeId !== null && this.filters.themeId !== undefined) {
            query.push(["themeId", String(this.filters.themeId)]);
        }

        if (this.filters.status !== null && this.filters.status !== undefined) {
            for (const value of this.filters.status.values) {
                query.push(["statusName", value]);
            }
        }

        if (this.filters.type !== null && this.filters.type !== undefined) {
            query.push(["advice", String(this.filters.type)]);
        }

        if (this.filters.startSubmissionDate !== null && this.filters.startSubmissionDate !== undefined) {
            query.push(["startSubmissionDate", String(new Date(this.filters.startSubmissionDate).toISOString())]);
        }

        if (this.filters.endSubmissionDate !== null && this.filters.endSubmissionDate !== undefined) {
            query.push(["endSubmissionDate", String(new Date(this.filters.endSubmissionDate).toISOString())]);
        }

        if (this.archives) {
            query.push(["archives", String(this.archives)]);
        }

        if (this.filters.responsibleDrinking != null) {
            query.push(["responsibleDrinking", String(this.filters.responsibleDrinking)]);
        }

        if (this.filters.sustainabilityClaim != null) {
            query.push(["sustainabilityClaim", String(this.filters.sustainabilityClaim)]);
        }

        if (this.filters.nonAlcoholicProduct != null) {
            query.push(["nonAlcoholicProduct", String(this.filters.nonAlcoholicProduct)]);
        }

        if (this.filters.rtd != null) {
            query.push(["rtd", String(this.filters.rtd)]);
        }


        return query;
    }

    private isImage(extension: string): boolean {
        switch (extension?.toLowerCase()) {
            case "jpg":
            case "jpeg":
            case "gif":
            case "bmp":
            case "png": {
                return true;
            }
            default:
                return false;
        }
    }

    private isExtensionType(extension: string): string {
        switch (extension?.toLowerCase()) {
            case "pdf": {
                return "pdf";
            }
            case "mpeg":
            case "avi":
            case "mp4":
            case "m4v":
            case "mkv": {
                return "mp4";
            }
            case "mp3": {
                return "mp3";
            }
            case "doc":
            case "docx": {
                return "doc";
            }
            case "xls":
            case "xlsx": {
                return "xls";
            }
            case "ppt":
            case "pptx": {
                return "ppt";
            }
            default:
                return "";
        }
    }

    private cacheFilters(): void {
        cacheService.setRequestFiltersToCache(this.filters, this.archives);
    }

    private retrieveFilters(): void {
        const filters = cacheService.getRequestFiltersFromCache(this.archives);
        if (filters) {
            this.filters = filters;
        }
        else {
            this.InitializeFilters();
        }
    }

    private InitializeFilters(): void {
        this.filters = {
            brandId: null,
            countryId: null,
            status: null,
            type: null,
            requestText: null,
            startSubmissionDate: null,
            endSubmissionDate: null,
            responsibleDrinking: null,
            sustainabilityClaim: null,
            nonAlcoholicProduct: null,
            rtd: null,
            currentPage: 1,
            themeId: null,
        }
    }
}
